import React from 'react';
import { motion, useTransform, type MotionValue } from 'framer-motion';
import { Bot, Folder, PanelLeft, PlugZap, Radar, Radio, Search, Sparkles, SquarePen, Users } from 'lucide-react';

// The dashboard-icons CDN is missing marks for several of these (HubSpot,
// Snowflake, QuickBooks, Intercom), which rendered as broken images. Resolving
// through each brand's own domain is the pattern used elsewhere in the project
// and covers every tool we reference.
const TOOL_DOMAINS: Record<string, string> = {
  slack: 'slack.com',
  gmail: 'mail.google.com',
  notion: 'notion.so',
  salesforce: 'salesforce.com',
  linear: 'linear.app',
  hubspot: 'hubspot.com',
  snowflake: 'snowflake.com',
  stripe: 'stripe.com',
  quickbooks: 'quickbooks.intuit.com',
  figma: 'figma.com',
  intercom: 'intercom.com',
  ahrefs: 'ahrefs.com',
  github: 'github.com',
  sentry: 'sentry.io',
  mailchimp: 'mailchimp.com',
  granola: 'granola.ai',
  microsoftteams: 'teams.microsoft.com',
  // AI surfaces the connections are exposed to.
  giga: 'getgiga.com',
  chatgpt: 'openai.com',
  claude: 'claude.ai',
  gemini: 'gemini.google.com',
  // grok.com carries Grok's own mark; x.ai's favicon is the plain X wordmark,
  // which reads as SpaceX rather than the product.
  grok: 'grok.com'
};
export const toolLogo = (slug: string) => `https://www.google.com/s2/favicons?domain=${TOOL_DOMAINS[slug] ?? `${slug}.com`}&sz=64`;

// Favicons are 64px rasters that bake in their own white plate and their own
// internal padding, so a row of them never matches optically. Where a brand has
// a clean vector mark, prefer it: transparent, crisp at any size, and drawn in
// the brand's own colour.
// Slack asked Simple Icons to drop its mark, so that slug 404s — it has to
// keep resolving through the favicon.
const BRAND_MARKS: Record<string, string> = {
  salesforce: 'salesforce/00A1E0',
  notion: 'notion/2B211A',
  linear: 'linear/5E6AD2',
  jira: 'jira/0052CC',
  hubspot: 'hubspot/FF7A59',
  intercom: 'intercom/1F8DED',
  github: 'github/2B211A',
  figma: 'figma/F24E1E',
  stripe: 'stripe/635BFF',
  googledrive: 'googledrive/1A73E8',
  googlesheets: 'googlesheets/34A853',
  gmail: 'gmail/EA4335',
  zoom: 'zoom/0B5CFF',
  asana: 'asana/F06A6A',
  confluence: 'confluence/172B4D',
  microsoftteams: 'microsoftteams/6264A7',
  // AI surfaces. Their favicons bake in a white plate, so the transparent
  // vector matters more here than anywhere — but Simple Icons can drop or
  // rename either slug, hence the runtime fallback in BrandLogo.
  chatgpt: 'openai/2B211A',
  claude: 'claude/D97757',
  gemini: 'googlegemini/4285F4',
  // The directory marquee. Without these, each of these brands resolved to a
  // 64px Google favicon — and a favicon bakes in its own opaque plate, which
  // is what produced a white or coloured square sitting inside our own tile.
  // A vector mark is transparent, so the tile is the only container.
  // Slack asked Simple Icons to drop its mark, so this slug may 404 — worth
  // attempting anyway, since BrandLogo falls back to the favicon on error.
  slack: 'slack/E01E5A',
  shopify: 'shopify/95BF47',
  zendesk: 'zendesk/03363D',
  dropbox: 'dropbox/0061FF',
  airtable: 'airtable/18BFFF',
  clickup: 'clickup/7B68EE',
  trello: 'trello/0052CC',
  zapier: 'zapier/FF4F00',
  gitlab: 'gitlab/FC6D26',
  snowflake: 'snowflake/29B5E8',
  quickbooks: 'quickbooks/2CA01C',
  mailchimp: 'mailchimp/FFE01B'
};

/**
 * Marks drawn in near-black or a very dark brand colour. Rendered on a dark
 * ground they all but vanish, so on dark they are redrawn in white — this is
 * exactly the substitution a brand's own guidelines make for a dark
 * background, not an invert filter, which would also mangle the coloured
 * marks beside them.
 */
const DARK_GROUND_MARKS: Record<string, string> = {
  github: 'github/FFFFFF',
  notion: 'notion/FFFFFF',
  chatgpt: 'openai/FFFFFF',
  zendesk: 'zendesk/FFFFFF',
  confluence: 'confluence/FFFFFF',
  // Mailchimp's full-colour mark is a near-black Freddie that relies on its
  // yellow plate to be seen, and Zapier's is dark enough to disappear too.
  // Redrawn in the brand's own bright accent rather than inverted — an invert
  // would shift the hue as well as the value and stop being the brand colour.
  mailchimp: 'mailchimp/FFE01B',
  zapier: 'zapier/FF6B35',
  // Grok's mark is drawn in pure black, so on a dark ground its colour SVG is
  // an invisible square. Needed now that the Connections screen renders marks
  // in full colour rather than as flattened silhouettes.
  grok: 'grok/FFFFFF'
};

/**
 * Giga's own mark is not on any icon CDN. This is the same Vector.png the site
 * header uses — the product screens were pointing at a different, older export
 * of the mark, which is why Giga read as slightly wrong beside real brand logos.
 */
const GIGA_MARK = 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png';

/**
 * Brands whose real mark is MULTICOLOUR. Simple Icons only publishes flat
 * single-colour glyphs, which is why Figma came out as one orange shape and
 * Gmail as a flat red envelope. Worse, Google had its marks pulled from Simple
 * Icons entirely, so Gmail and Drive were silently falling through to a
 * favicon — a raster with its own opaque plate, hence the square-in-a-square.
 * These resolve to the brand's actual full-colour SVG instead.
 */
const COLOUR_MARKS: Record<string, string> = {
  gmail: 'gmail',
  googledrive: 'google-drive',
  googlesheets: 'google-sheets',
  figma: 'figma',
  // Zapier deliberately NOT here: its real mark is a single-colour orange
  // flower, and the full-colour source was serving a different, dark variant of
  // it. The monochrome glyph in the brand's own #FF4F00 is the correct mark.
  slack: 'slack',
  microsoftteams: 'microsoft-teams',
  dropbox: 'dropbox',
  airtable: 'airtable',
  trello: 'trello',
  clickup: 'clickup',
  mailchimp: 'mailchimp',
  intercom: 'intercom',
  zoom: 'zoom',
  jira: 'jira',
  confluence: 'confluence',
  salesforce: 'salesforce',
  shopify: 'shopify'
};
const colourMark = (name: string) => `https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/${name}.svg`;

/**
 * Every source we can resolve a mark from, best first: the brand's real
 * full-colour SVG, then a single-colour vector glyph, then the favicon as a
 * last resort. Returned as a chain rather than one URL because any of these
 * CDNs can drop or rename a slug without warning — BrandLogo walks the chain
 * on error so a missing mark degrades instead of breaking.
 *
 * On a dark ground the monochrome vector WINS over the colour SVG for the
 * marks that are drawn in ink, since only that source can redraw them white.
 */
const brandMarkChain = (slug: string, onDark = false): string[] => {
  if (slug === 'giga') return [GIGA_MARK];
  const colour = COLOUR_MARKS[slug] ? colourMark(COLOUR_MARKS[slug]) : null;
  const mono = BRAND_MARKS[slug] ? `https://cdn.simpleicons.org/${BRAND_MARKS[slug]}` : null;
  const light = onDark && DARK_GROUND_MARKS[slug] ? `https://cdn.simpleicons.org/${DARK_GROUND_MARKS[slug]}` : null;
  return [light, colour, mono, toolLogo(slug)].filter((s): s is string => Boolean(s));
};

/** The best available mark for a slug. */
export const brandMark = (slug: string, onDark = false) => brandMarkChain(slug, onDark)[0];

// Optical sizing. Every mark is given the same box, but brands bake wildly
// different padding into their own artwork — OpenAI's knot in particular sits
// small inside a lot of empty space, so at an identical box it reads a size
// smaller than Slack or Notion beside it. These multipliers correct the drawn
// mark to the same visual weight; the box itself never changes.
const LOGO_SCALE: Record<string, number> = {
  chatgpt: 1.3,
  claude: 1.1,
  granola: 1.1
};

/**
 * Marks we draw ourselves rather than fetch. OpenAI asked Simple Icons to drop
 * its glyph, so every remote source for ChatGPT collapsed to the favicon — a
 * raster that bakes in its own opaque white plate, which is why the mark showed
 * up as a white square on a dark row. Drawn inline it is a transparent glyph in
 * currentColor, so it takes white on dark and ink on light like every other
 * single-colour mark.
 */
const INLINE_MARKS: Record<string, string> = {
  chatgpt: 'M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm12.0993 2.6069l-5.8144-3.3543 2.0201-1.1638a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.6813zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 8.0976V5.7652a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6115-1.4997Z'
};

/**
 * Simple Icons drops marks whenever a brand asks it to, so any slug in the map
 * can start 404ing without warning. Rendering through a component lets a failed
 * vector fall back to the favicon at runtime instead of leaving a broken image,
 * and gives every logo one place to be optically sized.
 */
export function BrandLogo({
  slug,
  className = '',
  onDark = false,
  mono = false
}: {
  slug: string;
  className?: string;
  /** Redraws ink-coloured marks in white so they read on a dark ground. */
  onDark?: boolean;
  /**
   * Renders every mark as a single-value silhouette — white on a dark ground,
   * ink on a light one. Twenty-eight brands' worth of unrelated corporate
   * colour is the noisiest thing in a directory; flattened, the marks read as
   * OUR set rather than as a collage of other companies' branding.
   *
   * Done with brightness/invert rather than grayscale: grayscale leaves each
   * brand at its own luminance, so the row still reads as unevenly weighted.
   */
  mono?: boolean;
}) {
  const chain = React.useMemo(() => brandMarkChain(slug, onDark), [slug, onDark]);
  const [step, setStep] = React.useState(0);
  React.useEffect(() => setStep(0), [chain]);
  const scale = LOGO_SCALE[slug];
  const inline = INLINE_MARKS[slug];
  if (inline) {
    return <svg viewBox="0 0 24 24" aria-hidden="true" className={`${className} shrink-0`} style={{
      fill: onDark ? '#FFFFFF' : '#2B211A'
    }} data-id="element-339" data-mp="1wansks6l90pf">
        <path d={inline} data-id="element-340" data-mp="1n96h5n44ln72" />
      </svg>;
  }
  const filter = mono ? onDark ? 'brightness(0) invert(1)' : 'brightness(0)' : undefined;
  return <img src={chain[Math.min(step, chain.length - 1)]} alt="" aria-hidden="true" onError={() => setStep(s => Math.min(s + 1, chain.length - 1))} className={`${className} object-contain`} style={scale || filter ? {
    transform: scale ? `scale(${scale})` : undefined,
    filter
  } : undefined} data-id="element-341" data-mp="2aztqmctowkvq" />;
}

/**
 * Shared chrome for every real-product screenshot on the page. One frame means
 * Connections, the platform primitives and the brain all read as the same
 * application rather than four unrelated illustrations.
 */
export function AppChrome({
  children,
  sidebar = true,
  active,
  className = '',
  sidebarCollapse,
  dark = false
}: {
  children: React.ReactNode;
  sidebar?: boolean;
  active?: string;
  className?: string;
  /** Dark glass frame: the chrome recedes and the content cards carry the page. */
  dark?: boolean;
  /**
   * 0 = sidebar open, 1 = fully collapsed. Pass a scroll-linked value to have
   * the app frame set itself aside as the reader arrives: the screenshot first
   * reads as the whole product, then narrows to the one surface being argued.
   */
  sidebarCollapse?: MotionValue<number>;
}) {
  // No browser bar: the fake URL and window chrome added a frame around the
  // frame without telling anyone anything they didn't already know.
  // Two-part shadow: a 1px contact edge keeps the frame crisp against cream,
  // while the long soft cast gives it real elevation rather than a flat drop.
  return <div className={`overflow-hidden rounded-[20px] border transition-[background-color,border-color,box-shadow] duration-700 ease-out ${dark ? 'border-white/[0.14] bg-white/[0.055] shadow-[inset_0_1px_0_rgba(255,255,255,0.10),0_30px_90px_-40px_rgba(0,0,0,0.8)] backdrop-blur-2xl' : 'border-[#E7E0D4] bg-white shadow-[0_1px_2px_rgba(43,33,26,0.05),0_8px_24px_-12px_rgba(43,33,26,0.18),0_40px_100px_-40px_rgba(43,33,26,0.45)]'} ${className}`} data-id="element-342" data-mp="25zkxttcq3fog">
      <div className="flex h-full min-h-[340px] sm:min-h-[420px]" data-id="element-343" data-mp="vdvn6w2ghivc">
        {sidebar && (sidebarCollapse ? <CollapsibleSidebar active={active} collapse={sidebarCollapse} dark={dark} data-id="element-344" data-mp="3tw0xnotqe0ex" /> : <AppSidebar active={active} dark={dark} data-id="element-345" data-mp="2oopu0eqmzbo" />)}
        <div className={`min-w-0 flex-1 transition-colors duration-700 ease-out ${dark ? 'bg-transparent' : 'bg-white'}`} data-id="element-346" data-mp="2phmu9nwaagm">
          {children}
        </div>
      </div>
    </div>;
}

/**
 * The sidebar as a scroll-driven reveal-then-recede. It holds its full width
 * while the reader arrives, then slides out of the frame — so the screenshot
 * establishes itself as the product before narrowing to Connections alone.
 * Width and translate move together so the panel LEAVES rather than squashing.
 */
function CollapsibleSidebar({
  active,
  collapse,
  dark = false
}: {
  active?: string;
  collapse: MotionValue<number>;
  dark?: boolean;
}) {
  const width = useTransform(collapse, [0, 1], [196, 0]);
  const x = useTransform(collapse, [0, 1], [0, -60]);
  const opacity = useTransform(collapse, [0, 0.7], [1, 0]);
  return <motion.div style={{
    width
  }} className="hidden shrink-0 overflow-hidden lg:block" data-id="element-347" data-mp="1qkwa1w88o0n9">
      <motion.div style={{
      x,
      opacity
    }} className="h-full w-[196px]" data-id="element-348" data-mp="25dpy461ktp3j">
        <AppSidebar active={active} dark={dark} data-id="element-349" data-mp="20c93gs9oqyve" />
      </motion.div>
    </motion.div>;
}
const SIDEBAR_CHATS = ['Inbox catch-up', 'Renewal risk', 'Board deck notes'];
function AppSidebar({
  active,
  dark = false
}: {
  active?: string;
  dark?: boolean;
}) {
  return <aside className={`hidden w-[196px] shrink-0 flex-col border-r px-2.5 py-3 lg:flex ${dark ? 'border-white/10 bg-white/[0.03]' : 'border-[#EFEAE1] bg-[#FCFAF6]'}`} data-id="element-350" data-mp="jzbufp85xbo2">
      <div className="mb-3 flex items-center justify-between px-1.5" data-id="element-351" data-mp="2qmszurksqq72">
        <span className="flex items-center gap-2" data-id="element-352" data-mp="2qom6w7zbncfl">
          <span className="flex size-6 items-center justify-center rounded-lg bg-[#134E4A] text-[10px] font-bold text-white" data-id="element-353" data-mp="2vlvwpoqmhq3y">
            BW
          </span>
          <span className={`text-[12.5px] font-semibold ${dark ? 'text-white' : 'text-[#2B211A]'}`} data-id="element-354" data-mp="2hsejsmsfzbmw">
            Brightwave
          </span>
        </span>
        <PanelLeft size={13} className={dark ? 'text-white/30' : 'text-neutral-300'} data-id="element-355" data-mp="27v9y0o2idi6m" />
      </div>
      <div className={`mb-3 flex items-center gap-2 rounded-xl border px-2.5 py-1.5 ${dark ? 'border-white/12 bg-white/[0.06]' : 'border-[#E7E0D4] bg-white'}`} data-id="element-356" data-mp="2cvxnh5b39qyq">
        <SquarePen size={12} className={dark ? 'text-white/45' : 'text-neutral-400'} data-id="element-357" data-mp="22wov2gvvgkeh" />
        <span className={`text-[12px] font-semibold ${dark ? 'text-white' : 'text-[#2B211A]'}`} data-id="element-358" data-mp="3t9ecvkmm5uhz">
          New chat
        </span>
      </div>
      <div className={`mb-1 flex items-center gap-2 rounded-lg px-2 py-1.5 ring-1 ${dark ? 'bg-white/[0.04] ring-white/10' : 'bg-white ring-[#EFEAE1]'}`} data-id="element-359" data-mp="14k9dn4yujc7">
        <Search size={11} className={dark ? 'text-white/30' : 'text-neutral-300'} data-id="element-360" data-mp="153fhbli7mldf" />
        <span className={`text-[11.5px] ${dark ? 'text-white/45' : 'text-neutral-400'}`} data-id="element-361" data-mp="155jsmoifumnq">
          Search
        </span>
      </div>
      <SidebarGroup label="Manage" items={MANAGE_ITEMS} active={active} dark={dark} data-id="element-362" data-mp="w3al87igv9ek" />
      <SidebarGroup label="Brain" items={BRAIN_ITEMS} active={active} dark={dark} data-id="element-363" data-mp="1st0asybzp10y" />
      <p className={`mt-3 px-2 pb-1 text-[9.5px] font-bold uppercase tracking-[0.09em] ${dark ? 'text-white/40' : 'text-neutral-400'}`} data-id="element-364" data-mp="1suumrn5a9isw">
        Chats
      </p>
      <ul data-id="element-365" data-mp="1jzex7ibk9pt4">
        {SIDEBAR_CHATS.map(c => <li key={c} className={`truncate rounded-lg px-2 py-[5px] text-[12px] font-medium ${dark ? 'text-white/55' : 'text-neutral-500'}`} data-id="element-366" data-mp="1jydauhp3hgw3">
            {c}
          </li>)}
      </ul>
    </aside>;
}
const BRAIN_ITEMS = [{
  label: 'Tracks',
  icon: Folder
}, {
  label: 'Scans',
  icon: Radar
}, {
  label: 'Observations',
  icon: Sparkles
}];
const MANAGE_ITEMS = [{
  label: 'Assistants',
  icon: Bot
}, {
  label: 'Pulses',
  icon: Radio
}, {
  label: 'Connections',
  icon: PlugZap
}, {
  label: 'Groups',
  icon: Users
}];
function SidebarGroup({
  label,
  items,
  active,
  dark = false
}: {
  label: string;
  items: Array<{
    label: string;
    icon: React.ElementType;
  }>;
  active?: string;
  dark?: boolean;
}) {
  return <>
      <p className={`mt-3 px-2 pb-1 text-[9.5px] font-bold uppercase tracking-[0.09em] ${dark ? 'text-white/40' : 'text-neutral-400'}`} data-id="element-367" data-mp="2y12xi4fffdm6">
        {label}
      </p>
      <ul data-id="element-368" data-mp="37q4dhulz1v05">
        {items.map(item => {
        const isActive = active === item.label;
        return <li key={item.label} className={`flex items-center gap-2 rounded-lg px-2 py-[5px] text-[12px] ${isActive ? dark ? 'bg-white/[0.12] font-semibold text-white' : 'bg-[#F1ECE3] font-semibold text-[#2B211A]' : dark ? 'font-medium text-white/55' : 'font-medium text-neutral-500'}`} data-id="element-369" data-mp="37sgvg7rqc5d7">
              <item.icon size={11} strokeWidth={1.9} className={`shrink-0 ${isActive ? dark ? 'text-white' : 'text-[#2B211A]' : dark ? 'text-white/35' : 'text-neutral-300'}`} data-id="element-370" data-mp="3lnsuusgtyk0g" />
              <span className="truncate" data-id="element-371" data-mp="5vtpfx2ln6jn">{item.label}</span>
            </li>;
      })}
      </ul>
    </>;
}